// src/components/LabTest/AllPackagesAPI.js

import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');

// ✅ Fetch all packages from API
export const fetchAllPackagesFromAPI = async () => {
  let allPackages = [];
  let currentPageNum = 1;
  let hasMorePages = true;
  
  while (hasMorePages) {
    const response = await axios.get(
      `${API_BASE_URL}/MasterAPI/GetTestPackagesAll?pageNumber=${currentPageNum}&pageSize=100`
    );
    
    if (response.data && response.data.succeeded) {
      const data = response.data.data || [];
      const pageData = Array.isArray(data) ? data : [];
      allPackages = [...allPackages, ...pageData];
      
      const totalPagesFromAPI = response.data.totalPages || 1;
      hasMorePages = currentPageNum < totalPagesFromAPI;
      currentPageNum++;
    } else {
      hasMorePages = false;
    }
  }
  
  // Filter: Only keep packages where isActive === true
  return allPackages.filter(pkg => pkg.isActive === true);
};

// ✅ Get user ID from localStorage
export const getUserId = () => {
  const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
  return user?.userId || null;
};

// ✅ Package price calculation helper
export const calculatePackagePrice = (pkg) => {
  const finalPrice = Number(pkg.packagePrice) || 0;
  const originalPrice = Number(pkg.totalPrice) || 0;
  const discountAmount = Number(pkg.discountPrice) || 0;
  const hasDiscount = discountAmount > 0 && finalPrice < originalPrice;
  const discountPercent = originalPrice > 0 && discountAmount > 0 
    ? Math.round((discountAmount / originalPrice) * 100) 
    : 0;
  
  return {
    finalPrice,
    originalPrice,
    discountAmount,
    hasDiscount,
    discountPercent
  };
};

// ✅ Get tests count from package
export const getTestsCount = (pkg) => {
  if (!pkg.testPackageDetailsJson) return 0;
  try {
    const tests = JSON.parse(pkg.testPackageDetailsJson);
    return Array.isArray(tests) ? tests.length : 0;
  } catch {
    return 0;
  }
};

// ✅ Get unique labs from packages
export const getUniqueLabs = (packages) => {
  return [...new Map(
    packages.filter(p => p.labId && p.labName)
      .map(pkg => [pkg.labId, pkg.labName])
  ).entries()].map(([id, name]) => ({ id, name }));
};

// ✅ Get unique companies from packages
export const getUniqueCompanies = (packages) => {
  return [...new Map(
    packages.filter(p => p.companyId && p.companyName)
      .map(pkg => [pkg.companyId, pkg.companyName])
  ).entries()].map(([id, name]) => ({ id, name }));
};

// ✅ Filter and sort packages
export const filterAndSortPackages = (packages, filters) => {
  let result = [...packages];

  const { searchTerm, selectedLab, selectedCompany, sortBy, priceRange } = filters;

  if (searchTerm) {
    result = result.filter(pkg =>
      pkg.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.labName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  if (selectedLab !== 'all') {
    result = result.filter(pkg => pkg.labId === parseInt(selectedLab));
  }

  if (selectedCompany !== 'all') {
    result = result.filter(pkg => pkg.companyId === parseInt(selectedCompany));
  }

  result = result.filter(pkg => 
    pkg.packagePrice >= priceRange.min && 
    pkg.packagePrice <= priceRange.max
  );

  switch (sortBy) {
    case 'popular':
      result.sort((a, b) => (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0));
      break;
    case 'price-low':
      result.sort((a, b) => (a.packagePrice || 0) - (b.packagePrice || 0));
      break;
    case 'price-high':
      result.sort((a, b) => (b.packagePrice || 0) - (a.packagePrice || 0));
      break;
    case 'discount':
      result.sort((a, b) => (b.discountPrice || 0) - (a.discountPrice || 0));
      break;
    case 'name':
      result.sort((a, b) => a.packageName?.localeCompare(b.packageName) || 0);
      break;
    default:
      break;
  }

  return result;
};